import { useEffect, useState } from 'react'

/**
 * Urgency tiers drive the KDS card visual treatment:
 *
 *   fresh    — < 50% of target (calm green)
 *   warming  — 50%–90%         (amber)
 *   urgent   — 90%–120%        (red, steady)
 *   critical — >= 120%         (red, pulsing)
 *   stale    — exceeds staleMinutes (striped/grey; hidden unless admin opts in)
 *
 * A scale gives real information density: a card isn't just "urgent/not";
 * cooks can see how urgent from across the line.
 */
export type UrgencyTier = 'fresh' | 'warming' | 'urgent' | 'critical' | 'stale'

/**
 * Share of the target prep time at which each tier starts. Exported so the
 * Kitchen Setup timing preview reads the same numbers the cards do and can
 * never describe a colour change the KDS doesn't make.
 */
export const KDS_TIER_RATIOS = { warming: 0.5, urgent: 0.9, critical: 1.2 } as const

/** Seconds after which a ticket goes stale — never sooner than a minute past target. */
export function kdsStaleSeconds(targetPrepMinutes: number, staleMinutes: number): number {
  const targetSeconds = Math.max(1, targetPrepMinutes * 60)
  return Math.max(targetSeconds + 60, staleMinutes * 60)
}

export interface UrgencyState {
  elapsedSeconds: number
  elapsedLabel: string
  /** Ratio of elapsed time vs target prep (0..∞). Useful for progress bars. */
  ratio: number
  /** Retained for backwards-compat with existing callers. */
  isUrgent: boolean
  tier: UrgencyTier
}

function formatClock(totalSeconds: number): string {
  const sign = totalSeconds < 0 ? '-' : ''
  const s = Math.abs(totalSeconds)
  const m = Math.floor(s / 60)
  const sec = s % 60
  return `${sign}${m.toString().padStart(2, '0')}:${sec.toString().padStart(2, '0')}`
}

export function useKdsUrgencyTimer(
  kotFirstSentAt: string | undefined,
  createdAt: string | undefined,
  targetPrepMinutes: number,
  staleMinutes = 120,
  tickMs = 1000,
): UrgencyState {
  const start = kotFirstSentAt || createdAt

  const [elapsedSeconds, setElapsedSeconds] = useState(() => {
    if (!start) return 0
    return Math.floor((Date.now() - new Date(start).getTime()) / 1000)
  })

  useEffect(() => {
    if (!start) return
    const id = window.setInterval(() => {
      setElapsedSeconds(Math.floor((Date.now() - new Date(start).getTime()) / 1000))
    }, tickMs)
    return () => window.clearInterval(id)
  }, [start, tickMs])

  const targetSeconds = Math.max(1, targetPrepMinutes * 60)
  const ratio = elapsedSeconds / targetSeconds
  const staleSeconds = kdsStaleSeconds(targetPrepMinutes, staleMinutes)

  let tier: UrgencyTier
  if (elapsedSeconds >= staleSeconds) tier = 'stale'
  else if (ratio >= KDS_TIER_RATIOS.critical) tier = 'critical'
  else if (ratio >= KDS_TIER_RATIOS.urgent) tier = 'urgent'
  else if (ratio >= KDS_TIER_RATIOS.warming) tier = 'warming'
  else tier = 'fresh'

  return {
    elapsedSeconds,
    elapsedLabel: formatClock(elapsedSeconds),
    ratio,
    isUrgent: tier === 'urgent' || tier === 'critical',
    tier,
  }
}
